import React, { useEffect, useMemo, useState, Component } from 'react';
import { X, Check, Search, Plus, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, FileText, CreditCard, Wallet, Calendar, Pencil, Minus, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { TagInput } from '../TagInput';
import type { TagData } from '../TagInput';
import { useTags } from '../../contexts/TagsContext';
import { useAppData } from '../../contexts/AppDataContext';
interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (subscriptionId: string) => void;
}
interface CustomerData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  internalNote: string;
  tags: TagData[];
  acquisitionSource: string;
  acquisitionSourceDetail: string;
}
interface AddressData {
  shipping: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  billing: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  billingSameAsShipping: boolean;
}
interface SubscriptionData {
  preference: string;
  bottleCount: number;
  frequency: string;
  wines: Array<{
    id: string;
    name: string;
    vintage: string;
    price: number;
    quantity: number;
  }>;
}
interface PaymentData {
  method: 'invoice' | 'saved-card';
  savedCardId?: string;
  firstBillingDate: string;
  sendWelcomeEmail: boolean;
}
const STEPS = [{
  number: 1,
  label: 'Customer'
}, {
  number: 2,
  label: 'Address'
}, {
  number: 3,
  label: 'Subscription'
}, {
  number: 4,
  label: 'Payment'
}, {
  number: 5,
  label: 'Review'
}];
const MOCK_WINES = [{
  id: '1',
  name: 'Fiano',
  vintage: '2025',
  price: 35,
  inStock: true
}, {
  id: '2',
  name: 'Shiraz',
  vintage: '2024',
  price: 45,
  inStock: true
}, {
  id: '3',
  name: 'Pinot Noir',
  vintage: '2023',
  price: 42,
  inStock: true
}, {
  id: '4',
  name: 'Chardonnay',
  vintage: '2024',
  price: 38,
  inStock: true
}, {
  id: '5',
  name: 'Cabernet Sauvignon',
  vintage: '2023',
  price: 48,
  inStock: false
}, {
  id: '6',
  name: 'Riesling',
  vintage: '2025',
  price: 32,
  inStock: true
}];
const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const AVAILABLE_TAGS = ['Cellar Door Signup', 'Phone Order', 'VIP', 'Gift', 'Corporate', 'Referral'];
// Mock existing contacts for search
const MOCK_CONTACTS = [{
  id: '1',
  firstName: 'Sarah',
  lastName: 'Johnson',
  email: 'sarah.j@example.com',
  phone: '+1 (555) 123-4567',
  status: 'Active'
}, {
  id: '2',
  firstName: 'Michael',
  lastName: 'Chen',
  email: 'michael.c@example.com',
  phone: '+1 (555) 987-6543',
  status: 'Paused'
}, {
  id: '3',
  firstName: 'Emma',
  lastName: 'Wilson',
  email: 'emma.w@example.com',
  phone: '+1 (555) 456-7890',
  status: 'Active'
}, {
  id: '4',
  firstName: 'James',
  lastName: 'Brown',
  email: 'james.b@example.com',
  phone: '+1 (555) 222-3333',
  status: 'Cancelled'
}, {
  id: '5',
  firstName: 'Olivia',
  lastName: 'Davis',
  email: 'olivia.d@example.com',
  phone: '+1 (555) 777-8888',
  status: 'Active'
}];
export function CreateSubscriptionModal({
  isOpen,
  onClose,
  onSuccess
}: CreateSubscriptionModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Get tags from shared context
  const {
    allTags,
    createTag
  } = useTags();
  // Get app data context for creating subscriptions
  const {
    createSubscription
  } = useAppData();
  // Form data
  const [customerData, setCustomerData] = useState<CustomerData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    internalNote: '',
    tags: [],
    acquisitionSource: 'Cellar Door',
    acquisitionSourceDetail: ''
  });
  const [addressData, setAddressData] = useState<AddressData>({
    shipping: {
      line1: '',
      line2: '',
      city: '',
      state: 'NSW',
      postcode: '',
      country: 'Australia'
    },
    billing: {
      line1: '',
      line2: '',
      city: '',
      state: 'NSW',
      postcode: '',
      country: 'Australia'
    },
    billingSameAsShipping: true
  });
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>({
    preference: 'Custom',
    bottleCount: 3,
    frequency: 'Every Month',
    wines: []
  });
  const [paymentData, setPaymentData] = useState<PaymentData>({
    method: 'invoice',
    firstBillingDate: new Date().toISOString().split('T')[0],
    sendWelcomeEmail: true
  });
  // Search and validation states
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateCustomer, setShowCreateCustomer] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);
  const resetForm = () => {
    setCurrentStep(1);
    setShowSuccess(false);
    setHasUnsavedChanges(false);
    setCustomerData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      internalNote: '',
      tags: [],
      acquisitionSource: 'Cellar Door',
      acquisitionSourceDetail: ''
    });
    setAddressData({
      shipping: {
        line1: '',
        line2: '',
        city: '',
        state: 'NSW',
        postcode: '',
        country: 'Australia'
      },
      billing: {
        line1: '',
        line2: '',
        city: '',
        state: 'NSW',
        postcode: '',
        country: 'Australia'
      },
      billingSameAsShipping: true
    });
    setSubscriptionData({
      preference: 'Custom',
      bottleCount: 3,
      frequency: 'Every Month',
      wines: []
    });
    setPaymentData({
      method: 'invoice',
      firstBillingDate: new Date().toISOString().split('T')[0],
      sendWelcomeEmail: true
    });
  };
  const handleClose = () => {
    if (hasUnsavedChanges && !showSuccess) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };
  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };
  const handleNext = () => {
    if (validateCurrentStep()) {
      setHasUnsavedChanges(true);
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const handleCreateSubscription = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Create the subscription object
    const newSubscription = {
      status: 'Active',
      customerName: `${customerData.firstName} ${customerData.lastName}`,
      customer: customerData.email,
      customerPhone: customerData.phone,
      location: `${addressData.shipping.city}, ${addressData.shipping.state}`,
      amount: `$${getSubtotal().toFixed(2)}`,
      subscriptionTitle: `${subscriptionData.preference} - ${subscriptionData.bottleCount} Pack`,
      discountPercent: 0,
      nextPayment: paymentData.firstBillingDate,
      startDate: new Date().toISOString().split('T')[0],
      orderCount: 0,
      tags: customerData.tags.map(tag => tag.id),
      items: subscriptionData.wines.map(wine => ({
        quantity: wine.quantity,
        name: `${wine.vintage} ${wine.name}`
      }))
    };
    // Create the subscription using the context
    const newSubId = createSubscription(newSubscription);
    setCreatedSubscriptionId(newSubId);
    setShowSuccess(true);
    setHasUnsavedChanges(false);
    if (onSuccess) {
      onSuccess(newSubId);
    }
  };
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return customerData.firstName.trim() !== '' && customerData.lastName.trim() !== '' && customerData.email.trim() !== '' && isValidEmail(customerData.email);
      case 2:
        return addressData.shipping.line1.trim() !== '' && addressData.shipping.city.trim() !== '' && addressData.shipping.postcode.trim() !== '';
      case 3:
        const totalBottles = subscriptionData.wines.reduce((sum, wine) => sum + wine.quantity, 0);
        return totalBottles === subscriptionData.bottleCount;
      case 4:
        return paymentData.firstBillingDate !== '';
      case 5:
        return true;
      default:
        return false;
    }
  };
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleEmailChange = (email: string) => {
    setCustomerData({
      ...customerData,
      email
    });
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    // Simulate duplicate check
    if (email === 'existing@example.com') {
      setDuplicateWarning(true);
    } else {
      setDuplicateWarning(false);
    }
  };
  const handleWineQuantityChange = (wineId: string, delta: number) => {
    setSubscriptionData(prev => {
      const existingWine = prev.wines.find(w => w.id === wineId);
      const wine = MOCK_WINES.find(w => w.id === wineId);
      if (!wine) return prev;
      if (!existingWine && delta > 0) {
        return {
          ...prev,
          wines: [...prev.wines, {
            id: wine.id,
            name: wine.name,
            vintage: wine.vintage,
            price: wine.price,
            quantity: 1
          }]
        };
      }
      return {
        ...prev,
        wines: prev.wines.map(w => {
          if (w.id === wineId) {
            const newQuantity = Math.max(0, w.quantity + delta);
            return {
              ...w,
              quantity: newQuantity
            };
          }
          return w;
        }).filter(w => w.quantity > 0)
      };
    });
  };
  const getWineQuantity = (wineId: string): number => {
    return subscriptionData.wines.find(w => w.id === wineId)?.quantity || 0;
  };
  const getTotalBottles = (): number => {
    return subscriptionData.wines.reduce((sum, wine) => sum + wine.quantity, 0);
  };
  const getSubtotal = (): number => {
    return subscriptionData.wines.reduce((sum, wine) => sum + wine.price * wine.quantity, 0);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-885">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm" onClick={handleClose} data-id="element-886" />

      {/* Modal */}
      <div className="relative w-full max-w-[680px] bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh]" data-id="element-887">
        {showSuccess ? <SuccessView subscriptionId={createdSubscriptionId} customerName={`${customerData.firstName} ${customerData.lastName}`} onViewSubscription={() => {
        // Close modal and return to subscriptions table
        if (onSuccess) {
          onSuccess(createdSubscriptionId);
        }
        onClose();
      }} onCreateAnother={() => {
        resetForm();
      }} data-id="element-888" /> : <>
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-[#E2E8F0]" data-id="element-889">
              <div className="flex items-center justify-between" data-id="element-890">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-891">
                  Create Subscription
                </h2>
                <button onClick={handleClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-892">
                  <X className="w-5 h-5" data-id="element-893" />
                </button>
              </div>
            </div>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} completedSteps={currentStep - 1} data-id="element-894" />

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-895">
              {currentStep === 1 && <Step1Customer data={customerData} onChange={setCustomerData} searchQuery={searchQuery} onSearchChange={setSearchQuery} showCreateCustomer={showCreateCustomer} onToggleCreateCustomer={setShowCreateCustomer} emailError={emailError} onEmailChange={handleEmailChange} duplicateWarning={duplicateWarning} allTags={allTags} onCreateTag={createTag} data-id="element-896" />}
              {currentStep === 2 && <Step2Address data={addressData} onChange={setAddressData} data-id="element-897" />}
              {currentStep === 3 && <Step3Subscription data={subscriptionData} onChange={setSubscriptionData} onWineQuantityChange={handleWineQuantityChange} getWineQuantity={getWineQuantity} getTotalBottles={getTotalBottles} data-id="element-898" />}
              {currentStep === 4 && <Step4Payment data={paymentData} onChange={setPaymentData} data-id="element-899" />}
              {currentStep === 5 && <Step5Review customerData={customerData} addressData={addressData} subscriptionData={subscriptionData} paymentData={paymentData} onEdit={setCurrentStep} getSubtotal={getSubtotal} data-id="element-900" />}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-901">
              <Button variant="outline" size="md" onClick={handleBack} disabled={currentStep === 1} iconLeft={<ChevronLeft className="w-4 h-4" data-id="element-903" />} data-id="element-902">
                Back
              </Button>
              <Button variant="primary" size="md" onClick={currentStep === 5 ? handleCreateSubscription : handleNext} disabled={!validateCurrentStep()} iconRight={currentStep < 5 ? <ChevronRight className="w-4 h-4" data-id="element-905" /> : undefined} data-id="element-904">
                {currentStep === 5 ? 'Create Subscription' : 'Next'}
              </Button>
            </div>
          </>}
      </div>

      {/* Discard Confirmation */}
      {showDiscardConfirm && <DiscardConfirmation onCancel={() => setShowDiscardConfirm(false)} onDiscard={handleDiscard} data-id="element-906" />}
    </div>;
}
// Step Indicator Component
function StepIndicator({
  currentStep,
  completedSteps
}: {
  currentStep: number;
  completedSteps: number;
}) {
  return <div className="flex-shrink-0 px-6 py-4 border-b border-[#E2E8F0]" data-id="element-907">
      <div className="flex items-center justify-between relative" data-id="element-908">
        {/* Progress Bar */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-[#E2E8F0]" data-id="element-909">
          <div className="h-full bg-[#0EA5E9] transition-all duration-300" style={{
          width: `${completedSteps / (STEPS.length - 1) * 100}%`
        }} data-id="element-910" />
        </div>

        {/* Steps */}
        {STEPS.map((step, index) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;
        const isUpcoming = step.number > currentStep;
        return <div key={step.number} className="flex flex-col items-center relative z-10" data-id="element-911">
              <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm
                  transition-all duration-200
                  ${isCompleted ? 'bg-[#0EA5E9] text-white' : isCurrent ? 'bg-[#0EA5E9] text-white scale-110' : 'bg-white border-2 border-[#CBD5E1] text-[#94A3B8]'}
                `} data-id="element-912">
                {isCompleted ? <Check className="w-5 h-5" data-id="element-913" /> : <span data-id="element-914">{step.number}</span>}
              </div>
              <span className={`
                  mt-2 text-xs font-medium hidden sm:block
                  ${isCurrent ? 'text-[#0EA5E9]' : 'text-[#64748B]'}
                `} data-id="element-915">
                {step.label}
              </span>
            </div>;
      })}
      </div>
    </div>;
}
// Step 1: Customer
function Step1Customer({
  data,
  onChange,
  searchQuery,
  onSearchChange,
  showCreateCustomer,
  onToggleCreateCustomer,
  emailError,
  onEmailChange,
  duplicateWarning,
  allTags,
  onCreateTag
}: any) {
  const [showResults, setShowResults] = useState(false);
  const [selectedContact, setSelectedContact] = useState<(typeof MOCK_CONTACTS)[0] | null>(null);
  const filteredContacts = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length < 1) return [];
    const q = searchQuery.toLowerCase();
    return MOCK_CONTACTS.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.includes(q));
  }, [searchQuery]);
  const handleSelectContact = (contact: (typeof MOCK_CONTACTS)[0]) => {
    setSelectedContact(contact);
    setShowResults(false);
    onSearchChange('');
    onChange({
      ...data,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone
    });
    onToggleCreateCustomer(true);
  };
  const handleClearSelection = () => {
    setSelectedContact(null);
    onChange({
      ...data,
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };
  const handleAddTag = (tag: TagData) => {
    onChange({
      ...data,
      tags: [...data.tags, tag]
    });
  };
  const handleRemoveTag = (tagId: string) => {
    onChange({
      ...data,
      tags: data.tags.filter((t: TagData) => t.id !== tagId)
    });
  };
  const handleCreateNewTag = (label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    onCreateTag(newTag);
    handleAddTag(newTag);
  };
  return <div className="space-y-6" data-id="element-916">
      <div data-id="element-917">
        <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-918">
          Find or create customer
        </label>

        {/* Selected contact chip */}
        {selectedContact && <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md" data-id="element-919">
            <div className="flex-1" data-id="element-920">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-921">
                {selectedContact.firstName} {selectedContact.lastName}
              </span>
              <span className="text-sm text-[#64748B] ml-2" data-id="element-922">
                {selectedContact.email}
              </span>
            </div>
            <button onClick={handleClearSelection} className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-923">
              <X className="w-4 h-4" data-id="element-924" />
            </button>
          </div>}

        <div className="relative" data-id="element-925">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" data-id="element-926" />
          <input type="text" value={searchQuery} onChange={e => {
          onSearchChange(e.target.value);
          setShowResults(true);
        }} onFocus={() => setShowResults(true)} placeholder="Search by name, email, or phone..." className="w-full h-10 pl-10 pr-4 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-927" />

          {/* Search Results Dropdown */}
          {showResults && searchQuery.trim().length > 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-50 max-h-[240px] overflow-y-auto" data-id="element-928">
              {filteredContacts.length > 0 ? <>
                  <div className="px-3 py-2 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider border-b border-[#E2E8F0]" data-id="element-929">
                    Existing Contacts
                  </div>
                  {filteredContacts.map(contact => <button key={contact.id} onClick={() => handleSelectContact(contact)} className="w-full text-left px-3 py-2.5 hover:bg-[#F8FAFC] transition-colors flex items-center gap-3 border-b border-[#F1F5F9] last:border-b-0" data-id="element-930">
                      <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0" data-id="element-931">
                        <span className="text-xs font-semibold text-[#0284C7]" data-id="element-932">
                          {contact.firstName[0]}
                          {contact.lastName[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-933">
                        <div className="text-sm font-medium text-[#0F172A]" data-id="element-934">
                          {contact.firstName} {contact.lastName}
                        </div>
                        <div className="text-xs text-[#64748B] truncate" data-id="element-935">
                          {contact.email} · {contact.phone}
                        </div>
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${contact.status === 'Active' ? 'bg-[#D1FAE5] text-[#059669]' : contact.status === 'Paused' ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-id="element-936">
                        {contact.status}
                      </span>
                    </button>)}
                </> : <div className="px-3 py-4 text-center" data-id="element-937">
                  <p className="text-sm text-[#64748B]" data-id="element-938">
                    No contacts found for "{searchQuery}"
                  </p>
                </div>}
              {/* Create new option */}
              <div className="border-t border-[#E2E8F0]" data-id="element-939">
                <button onClick={() => {
              setShowResults(false);
              setSelectedContact(null);
              onToggleCreateCustomer(true);
            }} className="w-full text-left px-3 py-2.5 hover:bg-[#F0F9FF] transition-colors flex items-center gap-2 text-[#0EA5E9]" data-id="element-940">
                  <Plus className="w-4 h-4" data-id="element-941" />
                  <span className="text-sm font-medium" data-id="element-942">
                    Create new customer
                  </span>
                </button>
              </div>
            </div>}
        </div>
        {!showCreateCustomer && !selectedContact && <button onClick={() => onToggleCreateCustomer(true)} className="mt-2 text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1" data-id="element-943">
            <Plus className="w-4 h-4" data-id="element-944" />
            Create new customer
          </button>}
      </div>

      {showCreateCustomer && <>
          <div className="grid grid-cols-2 gap-4" data-id="element-945">
            <div data-id="element-946">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-947">
                First Name <span className="text-[#EF4444]" data-id="element-948">*</span>
              </label>
              <input type="text" value={data.firstName} onChange={e => onChange({
            ...data,
            firstName: e.target.value
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-949" />
            </div>
            <div data-id="element-950">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-951">
                Last Name <span className="text-[#EF4444]" data-id="element-952">*</span>
              </label>
              <input type="text" value={data.lastName} onChange={e => onChange({
            ...data,
            lastName: e.target.value
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-953" />
            </div>
          </div>

          <div data-id="element-954">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-955">
              Email <span className="text-[#EF4444]" data-id="element-956">*</span>
            </label>
            <input type="email" value={data.email} onChange={e => onEmailChange(e.target.value)} className={`w-full h-10 px-3 bg-white border rounded-md text-sm focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none ${emailError ? 'border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-957" />
            {emailError && <p className="mt-1 text-xs text-[#EF4444]" data-id="element-958">{emailError}</p>}
          </div>

          {duplicateWarning && <div className="p-3 bg-[#FFFBEB] border border-[#FEF3C7] rounded-md flex gap-2" data-id="element-959">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" data-id="element-960" />
              <div className="flex-1" data-id="element-961">
                <p className="text-sm text-[#92400E]" data-id="element-962">
                  This customer already has an active subscription. Creating
                  another will give them multiple subscriptions.
                </p>
                <button className="mt-1 text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-963">
                  View existing subscription →
                </button>
              </div>
            </div>}

          <div data-id="element-964">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-965">
              Phone
            </label>
            <input type="tel" value={data.phone} onChange={e => onChange({
          ...data,
          phone: e.target.value
        })} placeholder="+61 4XX XXX XXX" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-966" />
          </div>

          <div data-id="element-967">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-968">
              Internal Note
            </label>
            <textarea value={data.internalNote} onChange={e => onChange({
          ...data,
          internalNote: e.target.value
        })} placeholder="Add notes about this subscription (e.g., Phone order - spoke with John)" rows={3} className="w-full px-3 py-2 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none resize-none" data-id="element-969" />
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-970">
              Only visible to staff, not the customer
            </p>
          </div>

          <div data-id="element-971">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-972">
              Tags
            </label>
            <TagInput tags={data.tags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateNewTag} placeholder="Add or create tag..." data-id="element-973" />
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-974">
              Type a tag name and press Enter to create a new tag
            </p>
          </div>

          <div data-id="element-975">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-976">
              Acquisition Source
            </label>
            <select value={data.acquisitionSource} onChange={e => {
          onChange({
            ...data,
            acquisitionSource: e.target.value,
            acquisitionSourceDetail: ''
          });
        }} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-977">
              <option value="Cellar Door" data-id="element-978">Cellar Door</option>
              <option value="Website" data-id="element-979">Website</option>
              <option value="Event" data-id="element-980">Event</option>
              <option value="Referral" data-id="element-981">Referral</option>
              <option value="Phone" data-id="element-982">Phone</option>
              <option value="Other" data-id="element-983">Other</option>
            </select>
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-984">
              Where did this member come from?
            </p>

            {data.acquisitionSource === 'Event' && <div className="mt-3" data-id="element-985">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-986">
                  Event Name
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Winter Wine Festival 2026" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-987" />
              </div>}

            {data.acquisitionSource === 'Referral' && <div className="mt-3" data-id="element-988">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-989">
                  Referred by
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Member name or email" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-990" />
              </div>}

            {data.acquisitionSource === 'Other' && <div className="mt-3" data-id="element-991">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-992">
                  Source
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Trade show, partner promotion" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-993" />
              </div>}
          </div>
        </>}
    </div>;
}
// Step 2: Address
function Step2Address({
  data,
  onChange
}: any) {
  return <div className="space-y-6" data-id="element-994">
      <div data-id="element-995">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-996">
          Shipping Address
        </h3>
        <div className="space-y-4" data-id="element-997">
          <div data-id="element-998">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-999">
              Address Line 1 <span className="text-[#EF4444]" data-id="element-1000">*</span>
            </label>
            <input type="text" value={data.shipping.line1} onChange={e => onChange({
            ...data,
            shipping: {
              ...data.shipping,
              line1: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1001" />
          </div>

          <div data-id="element-1002">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1003">
              Address Line 2
            </label>
            <input type="text" value={data.shipping.line2} onChange={e => onChange({
            ...data,
            shipping: {
              ...data.shipping,
              line2: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1004" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1005">
            <div data-id="element-1006">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1007">
                City <span className="text-[#EF4444]" data-id="element-1008">*</span>
              </label>
              <input type="text" value={data.shipping.city} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                city: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1009" />
            </div>
            <div data-id="element-1010">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1011">
                State <span className="text-[#EF4444]" data-id="element-1012">*</span>
              </label>
              <select value={data.shipping.state} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                state: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1013">
                {AUSTRALIAN_STATES.map(state => <option key={state} value={state} data-id="element-1014">
                    {state}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4" data-id="element-1015">
            <div data-id="element-1016">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1017">
                Postcode <span className="text-[#EF4444]" data-id="element-1018">*</span>
              </label>
              <input type="text" value={data.shipping.postcode} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                postcode: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1019" />
            </div>
            <div className="col-span-3" data-id="element-1020">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1021">
                Country <span className="text-[#EF4444]" data-id="element-1022">*</span>
              </label>
              <select value={data.shipping.country} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                country: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1023">
                <option value="Australia" data-id="element-1024">Australia</option>
                <option value="New Zealand" data-id="element-1025">New Zealand</option>
                <option value="United States" data-id="element-1026">United States</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div data-id="element-1027">
        <label className="flex items-center gap-2 cursor-pointer" data-id="element-1028">
          <input type="checkbox" checked={data.billingSameAsShipping} onChange={e => onChange({
          ...data,
          billingSameAsShipping: e.target.checked
        })} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-1029" />
          <span className="text-sm text-[#0F172A]" data-id="element-1030">
            Billing address same as shipping
          </span>
        </label>
      </div>

      {!data.billingSameAsShipping && <div data-id="element-1031">
          <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1032">
            Billing Address
          </h3>
          <div className="space-y-4" data-id="element-1033">
            <div data-id="element-1034">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1035">
                Address Line 1 <span className="text-[#EF4444]" data-id="element-1036">*</span>
              </label>
              <input type="text" value={data.billing.line1} onChange={e => onChange({
            ...data,
            billing: {
              ...data.billing,
              line1: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1037" />
            </div>

            <div data-id="element-1038">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1039">
                Address Line 2
              </label>
              <input type="text" value={data.billing.line2} onChange={e => onChange({
            ...data,
            billing: {
              ...data.billing,
              line2: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1040" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-1041">
              <div data-id="element-1042">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1043">
                  City <span className="text-[#EF4444]" data-id="element-1044">*</span>
                </label>
                <input type="text" value={data.billing.city} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                city: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1045" />
              </div>
              <div data-id="element-1046">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1047">
                  State <span className="text-[#EF4444]" data-id="element-1048">*</span>
                </label>
                <select value={data.billing.state} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                state: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1049">
                  {AUSTRALIAN_STATES.map(state => <option key={state} value={state} data-id="element-1050">
                      {state}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4" data-id="element-1051">
              <div data-id="element-1052">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1053">
                  Postcode <span className="text-[#EF4444]" data-id="element-1054">*</span>
                </label>
                <input type="text" value={data.billing.postcode} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                postcode: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1055" />
              </div>
              <div className="col-span-3" data-id="element-1056">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1057">
                  Country <span className="text-[#EF4444]" data-id="element-1058">*</span>
                </label>
                <select value={data.billing.country} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                country: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1059">
                  <option value="Australia" data-id="element-1060">Australia</option>
                  <option value="New Zealand" data-id="element-1061">New Zealand</option>
                  <option value="United States" data-id="element-1062">United States</option>
                </select>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Step 3: Subscription
function Step3Subscription({
  data,
  onChange,
  onWineQuantityChange,
  getWineQuantity,
  getTotalBottles
}: any) {
  const totalBottles = getTotalBottles();
  const isValid = totalBottles === data.bottleCount;
  return <div className="space-y-6" data-id="element-1063">
      <div className="flex justify-end" data-id="element-1064">
        <span className="px-3 py-1.5 bg-[#E0F2FE] text-[#0284C7] text-sm font-medium rounded-full" data-id="element-1065">
          {data.bottleCount} Bottles {data.frequency}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4" data-id="element-1066">
        <div data-id="element-1067">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1068">
            Preference
          </label>
          <select value={data.preference} onChange={e => onChange({
          ...data,
          preference: e.target.value
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1069">
            <option value="Custom" data-id="element-1070">Custom</option>
            <option value="Winery Selection" data-id="element-1071">Winery Selection</option>
          </select>
        </div>

        <div data-id="element-1072">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1073">
            Bottle Count
          </label>
          <select value={data.bottleCount} onChange={e => onChange({
          ...data,
          bottleCount: parseInt(e.target.value)
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1074">
            <option value={3} data-id="element-1075">3 Bottles</option>
            <option value={6} data-id="element-1076">6 Bottles</option>
            <option value={12} data-id="element-1077">12 Bottles</option>
          </select>
        </div>

        <div data-id="element-1078">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1079">
            Frequency
          </label>
          <select value={data.frequency} onChange={e => onChange({
          ...data,
          frequency: e.target.value
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1080">
            <option value="Every Month" data-id="element-1081">Every Month</option>
            <option value="Every 2 Months" data-id="element-1082">Every 2 Months</option>
            <option value="Every 3 Months" data-id="element-1083">Every 3 Months</option>
            <option value="Quarterly" data-id="element-1084">Quarterly</option>
            <option value="Every 6 Months" data-id="element-1085">Every 6 Months</option>
            <option value="Yearly" data-id="element-1086">Yearly</option>
          </select>
        </div>
      </div>

      <div data-id="element-1087">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1088">
          Select Wines
        </h3>
        <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0]" data-id="element-1089">
          {MOCK_WINES.map(wine => {
          const quantity = getWineQuantity(wine.id);
          const isSelected = quantity > 0;
          return <div key={wine.id} className={`p-4 flex items-center justify-between ${isSelected ? 'bg-[#F0F9FF]' : ''} ${!wine.inStock ? 'opacity-50' : ''}`} data-id="element-1090">
                <div className="flex-1" data-id="element-1091">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-1092">
                    {wine.vintage} {wine.name}
                  </div>
                  <div className="text-sm text-[#64748B] mt-0.5" data-id="element-1093">
                    ${wine.price.toFixed(2)}
                  </div>
                  {!wine.inStock && <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-xs rounded" data-id="element-1094">
                      Out of stock
                    </span>}
                </div>
                <div className="flex items-center gap-2" data-id="element-1095">
                  <button onClick={() => onWineQuantityChange(wine.id, -1)} disabled={!wine.inStock || quantity === 0} className="w-8 h-8 flex items-center justify-center border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1096">
                    <Minus className="w-4 h-4" data-id="element-1097" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium" data-id="element-1098">
                    {quantity}
                  </span>
                  <button onClick={() => onWineQuantityChange(wine.id, 1)} disabled={!wine.inStock} className="w-8 h-8 flex items-center justify-center border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1099">
                    <Plus className="w-4 h-4" data-id="element-1100" />
                  </button>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Validation Banner */}
      <div className={`p-3 rounded-md flex items-center gap-2 ${isValid ? 'bg-[#D1FAE5] border border-[#10B981]' : 'bg-[#FEE2E2] border border-[#EF4444]'}`} data-id="element-1101">
        {isValid ? <>
            <CheckCircle className="w-5 h-5 text-[#059669] flex-shrink-0" data-id="element-1102" />
            <span className="text-sm text-[#065F46]" data-id="element-1103">
              Bottle count is correct - {totalBottles} of {data.bottleCount}{' '}
              bottles selected
            </span>
          </> : <>
            <AlertTriangle className="w-5 h-5 text-[#DC2626] flex-shrink-0" data-id="element-1104" />
            <span className="text-sm text-[#991B1B]" data-id="element-1105">
              Please select {data.bottleCount} bottles (currently {totalBottles}{' '}
              selected)
            </span>
          </>}
      </div>
    </div>;
}
// Step 4: Payment
function Step4Payment({
  data,
  onChange
}: any) {
  const [selectedCardId, setSelectedCardId] = useState('card-1');
  const [hasSavedCards, setHasSavedCards] = useState(true);
  const MOCK_SAVED_CARDS = [{
    id: 'card-1',
    brand: 'Visa',
    last4: '4242',
    exp: '12/27'
  }, {
    id: 'card-2',
    brand: 'Mastercard',
    last4: '8888',
    exp: '03/26'
  }];
  return <div className="space-y-6" data-id="element-1106">
      <div data-id="element-1107">
        <div className="flex items-center justify-between mb-4" data-id="element-1108">
          <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-1109">
            Payment Method
          </h3>
          {/* Demo toggle for saved cards state */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-id="element-1110">
            <span className="text-[11px] font-medium text-[#94A3B8] uppercase tracking-wide" data-id="element-1111">
              Has saved cards
            </span>
            <button role="switch" aria-checked={hasSavedCards} onClick={() => setHasSavedCards(!hasSavedCards)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${hasSavedCards ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-1112">
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${hasSavedCards ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} data-id="element-1113" />
            </button>
          </label>
        </div>
        <div className="space-y-3" data-id="element-1114">
          <PaymentOption icon={<FileText className="w-5 h-5" data-id="element-1116" />} value="invoice" selected={data.method === 'invoice'} onSelect={() => onChange({
          ...data,
          method: 'invoice'
        })} title="Send invoice" description="Customer will receive a Shopify checkout link to complete payment" data-id="element-1115" />

          {/* Saved Card - Expandable */}
          <div className={`border-2 rounded-lg transition-all ${data.method === 'saved-card' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-1117">
            <button onClick={() => onChange({
            ...data,
            method: 'saved-card'
          })} className="w-full p-4 text-left" data-id="element-1118">
              <div className="flex items-start gap-3" data-id="element-1119">
                <div className={`flex-shrink-0 ${data.method === 'saved-card' ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-1120">
                  <Wallet className="w-5 h-5" data-id="element-1121" />
                </div>
                <div className="flex-1" data-id="element-1122">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-1123">
                    Use saved payment method
                  </div>
                  <div className="text-sm text-[#64748B] mt-0.5" data-id="element-1124">
                    Charge the customer's card on file
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${data.method === 'saved-card' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-1125">
                  {data.method === 'saved-card' && <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" data-id="element-1126" />}
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {data.method === 'saved-card' && <div className="px-4 pb-4 animate-[ocean-fade-in_200ms_ease-in-out]" data-id="element-1127">
                <div className="border-t border-[#E2E8F0] pt-4 space-y-4" data-id="element-1128">
                  {/* Saved Cards Section */}
                  {hasSavedCards && <>
                      <div data-id="element-1129">
                        <p className="text-[12px] font-medium text-[#94A3B8] uppercase tracking-wide mb-2" data-id="element-1130">
                          Saved cards
                        </p>
                        <div className="space-y-2" data-id="element-1131">
                          {MOCK_SAVED_CARDS.map(card => <button key={card.id} onClick={() => setSelectedCardId(card.id)} className={`w-full flex items-center gap-3 p-3 rounded-md border transition-all text-left ${selectedCardId === card.id ? 'border-[#0EA5E9] bg-white shadow-sm border-l-[3px]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-1132">
                              {/* Card brand icon placeholder */}
                              <div className={`w-10 h-7 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${card.brand === 'Visa' ? 'bg-[#1A1F71] text-white' : 'bg-[#EB001B] text-white'}`} data-id="element-1133">
                                {card.brand === 'Visa' ? 'VISA' : 'MC'}
                              </div>
                              <div className="flex-1" data-id="element-1134">
                                <span className="text-sm font-medium text-[#0F172A]" data-id="element-1135">
                                  {card.brand} ending in {card.last4}
                                </span>
                                <span className="text-xs text-[#94A3B8] ml-2" data-id="element-1136">
                                  Exp {card.exp}
                                </span>
                              </div>
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedCardId === card.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-1137">
                                {selectedCardId === card.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9]" data-id="element-1138" />}
                              </div>
                            </button>)}
                        </div>
                        <p className="text-[11px] text-[#94A3B8] mt-2" data-id="element-1139">
                          From customer's Stripe account
                        </p>
                      </div>

                      {/* Divider */}
                      <div className="relative" data-id="element-1140">
                        <div className="absolute inset-0 flex items-center" data-id="element-1141">
                          <div className="w-full border-t border-[#E2E8F0]" data-id="element-1142" />
                        </div>
                        <div className="relative flex justify-center" data-id="element-1143">
                          <span className="bg-[#F0F9FF] px-3 text-[12px] text-[#94A3B8] font-medium" data-id="element-1144">
                            or add new card
                          </span>
                        </div>
                      </div>
                    </>}

                  {/* New Card Section - Stripe Elements Placeholder */}
                  <div data-id="element-1145">
                    {!hasSavedCards && <p className="text-[12px] font-medium text-[#94A3B8] uppercase tracking-wide mb-2" data-id="element-1146">
                        Add card
                      </p>}
                    <div className="space-y-3" data-id="element-1147">
                      {/* Card Number */}
                      <div className="relative" data-id="element-1148">
                        <input type="text" placeholder="Card number" disabled className="w-full h-10 px-3 pr-24 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-1149" />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5" data-id="element-1150">
                          <div className="w-8 h-5 rounded-sm bg-[#1A1F71] flex items-center justify-center" data-id="element-1151">
                            <span className="text-[7px] font-bold text-white" data-id="element-1152">
                              VISA
                            </span>
                          </div>
                          <div className="w-8 h-5 rounded-sm bg-[#EB001B] flex items-center justify-center" data-id="element-1153">
                            <span className="text-[7px] font-bold text-white" data-id="element-1154">
                              MC
                            </span>
                          </div>
                          <div className="w-8 h-5 rounded-sm bg-[#006FCF] flex items-center justify-center" data-id="element-1155">
                            <span className="text-[7px] font-bold text-white" data-id="element-1156">
                              AMEX
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Expiry + CVC */}
                      <div className="grid grid-cols-2 gap-3" data-id="element-1157">
                        <input type="text" placeholder="MM / YY" disabled className="w-full h-10 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-1158" />
                        <input type="text" placeholder="CVC" disabled className="w-full h-10 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-1159" />
                      </div>
                    </div>
                    {/* Security Note */}
                    <div className="flex items-center gap-1.5 mt-3" data-id="element-1160">
                      <Lock className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-1161" />
                      <span className="text-[11px] text-[#94A3B8]" data-id="element-1162">
                        Securely processed by Stripe
                      </span>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      <div data-id="element-1163">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1164">
          Schedule
        </h3>
        <div className="space-y-4" data-id="element-1165">
          <div data-id="element-1166">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1167">
              First billing date
            </label>
            <div className="relative" data-id="element-1168">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" data-id="element-1169" />
              <input type="date" value={data.firstBillingDate} onChange={e => onChange({
              ...data,
              firstBillingDate: e.target.value
            })} className="w-full h-10 pl-10 pr-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1170" />
            </div>
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-1171">
              Customer will be charged on this date
            </p>
          </div>

          <div data-id="element-1172">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-1173">
              <input type="checkbox" checked={data.sendWelcomeEmail} onChange={e => onChange({
              ...data,
              sendWelcomeEmail: e.target.checked
            })} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-1174" />
              <div data-id="element-1175">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-1176">
                  Send welcome email
                </div>
                <div className="text-xs text-[#64748B]" data-id="element-1177">
                  Customer will receive a welcome email with portal access
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>;
}
function PaymentOption({
  icon,
  value,
  selected,
  onSelect,
  title,
  description
}: any) {
  return <button onClick={onSelect} className={`w-full p-4 border-2 rounded-lg text-left transition-all ${selected ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-1178">
      <div className="flex items-start gap-3" data-id="element-1179">
        <div className={`flex-shrink-0 ${selected ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-1180">
          {icon}
        </div>
        <div className="flex-1" data-id="element-1181">
          <div className="text-sm font-medium text-[#0F172A]" data-id="element-1182">{title}</div>
          <div className="text-sm text-[#64748B] mt-0.5" data-id="element-1183">{description}</div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-1184">
          {selected && <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" data-id="element-1185" />}
        </div>
      </div>
    </button>;
}
// Step 5: Review
function Step5Review({
  customerData,
  addressData,
  subscriptionData,
  paymentData,
  onEdit,
  getSubtotal
}: any) {
  return <div className="space-y-4" data-id="element-1186">
      <ReviewCard title="Customer" onEdit={() => onEdit(1)} content={<div className="space-y-2" data-id="element-1188">
            <div className="text-sm font-medium text-[#0F172A]" data-id="element-1189">
              {customerData.firstName} {customerData.lastName}
            </div>
            <div className="text-sm text-[#64748B]" data-id="element-1190">{customerData.email}</div>
            {customerData.phone && <div className="text-sm text-[#64748B]" data-id="element-1191">{customerData.phone}</div>}
            {customerData.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-1192">
                {customerData.tags.map((tag: TagData) => <span key={tag.id} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${tag.color === 'blue' ? 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]' : tag.color === 'green' ? 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]' : tag.color === 'orange' ? 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]' : tag.color === 'purple' ? 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]' : 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'}`} data-id="element-1193">
                    {tag.label}
                  </span>)}
              </div>}
            {customerData.acquisitionSource && <div className="flex items-center gap-4 text-sm mt-2" data-id="element-1194">
                <span className="text-[#64748B]" data-id="element-1195">Source:</span>
                <span className="text-[#0F172A] font-medium" data-id="element-1196">
                  {customerData.acquisitionSource}
                  {customerData.acquisitionSourceDetail && ` — ${customerData.acquisitionSourceDetail}`}
                </span>
              </div>}
          </div>} data-id="element-1187" />

      <ReviewCard title="Shipping Address" onEdit={() => onEdit(2)} content={<div className="space-y-1" data-id="element-1198">
            <div className="text-sm text-[#0F172A]" data-id="element-1199">
              {addressData.shipping.line1}
            </div>
            {addressData.shipping.line2 && <div className="text-sm text-[#0F172A]" data-id="element-1200">
                {addressData.shipping.line2}
              </div>}
            <div className="text-sm text-[#0F172A]" data-id="element-1201">
              {addressData.shipping.city}, {addressData.shipping.state}{' '}
              {addressData.shipping.postcode}
            </div>
            <div className="text-sm text-[#0F172A]" data-id="element-1202">
              {addressData.shipping.country}
            </div>
            {addressData.billingSameAsShipping && <div className="text-sm text-[#64748B] mt-2" data-id="element-1203">
                Billing: Same as shipping
              </div>}
          </div>} data-id="element-1197" />

      <ReviewCard title="Subscription" onEdit={() => onEdit(3)} content={<div className="space-y-3" data-id="element-1205">
            <div className="flex items-center gap-4 text-sm" data-id="element-1206">
              <span className="text-[#64748B]" data-id="element-1207">Preference:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1208">
                {subscriptionData.preference}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1209">
              <span className="text-[#64748B]" data-id="element-1210">Bottle Count:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1211">
                {subscriptionData.bottleCount} Bottles
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1212">
              <span className="text-[#64748B]" data-id="element-1213">Frequency:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1214">
                {subscriptionData.frequency}
              </span>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0]" data-id="element-1215">
              <div className="text-sm font-medium text-[#0F172A] mb-2" data-id="element-1216">
                Selected Wines:
              </div>
              <div className="space-y-1" data-id="element-1217">
                {subscriptionData.wines.map((wine: any) => <div key={wine.id} className="flex justify-between text-sm text-[#64748B]" data-id="element-1218">
                    <span data-id="element-1219">
                      {wine.vintage} {wine.name} × {wine.quantity}
                    </span>
                    <span data-id="element-1220">${(wine.price * wine.quantity).toFixed(2)}</span>
                  </div>)}
              </div>
              <div className="flex justify-between text-sm font-medium text-[#0F172A] mt-2 pt-2 border-t border-[#E2E8F0]" data-id="element-1221">
                <span data-id="element-1222">Subtotal:</span>
                <span data-id="element-1223">${getSubtotal().toFixed(2)}</span>
              </div>
            </div>
          </div>} data-id="element-1204" />

      <ReviewCard title="Payment" onEdit={() => onEdit(4)} content={<div className="space-y-2" data-id="element-1225">
            <div className="flex items-center gap-4 text-sm" data-id="element-1226">
              <span className="text-[#64748B]" data-id="element-1227">Payment Method:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1228">
                {paymentData.method === 'invoice' ? 'Send invoice' : 'Use saved payment method'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1229">
              <span className="text-[#64748B]" data-id="element-1230">First Billing Date:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1231">
                {new Date(paymentData.firstBillingDate).toLocaleDateString('en-AU')}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1232">
              <span className="text-[#64748B]" data-id="element-1233">Welcome Email:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-1234">
                {paymentData.sendWelcomeEmail ? 'Will be sent' : 'Will not be sent'}
              </span>
            </div>
          </div>} data-id="element-1224" />

      {customerData.internalNote && <ReviewCard title="Internal Note" content={<div className="text-sm text-[#64748B]" data-id="element-1236">
              {customerData.internalNote}
            </div>} data-id="element-1235" />}
    </div>;
}
function ReviewCard({
  title,
  onEdit,
  content
}: any) {
  return <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-id="element-1237">
      <div className="flex items-center justify-between mb-3" data-id="element-1238">
        <h4 className="text-sm font-semibold text-[#0F172A]" data-id="element-1239">{title}</h4>
        {onEdit && <button onClick={onEdit} className="text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-1240">
            <Pencil className="w-4 h-4" data-id="element-1241" />
          </button>}
      </div>
      {content}
    </div>;
}
// Success View
function SuccessView({
  subscriptionId,
  customerName,
  onViewSubscription,
  onCreateAnother
}: any) {
  return <div className="p-12 flex flex-col items-center justify-center text-center" data-id="element-1242">
      <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-6" data-id="element-1243">
        <Check className="w-8 h-8 text-[#059669]" data-id="element-1244" />
      </div>
      <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-1245">
        Subscription Created
      </h2>
      <div className="text-[#0EA5E9] font-medium mb-1" data-id="element-1246">{subscriptionId}</div>
      <p className="text-sm text-[#64748B] mb-8" data-id="element-1247">for {customerName}</p>
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm" data-id="element-1248">
        <Button variant="outline" size="md" onClick={onCreateAnother} className="flex-1" data-id="element-1249">
          Create Another
        </Button>
        <Button variant="primary" size="md" onClick={onViewSubscription} className="flex-1" data-id="element-1250">
          Done
        </Button>
      </div>
    </div>;
}
// Discard Confirmation
function DiscardConfirmation({
  onCancel,
  onDiscard
}: any) {
  return <div className="absolute inset-0 z-[1060] flex items-center justify-center p-4" data-id="element-1251">
      <div className="absolute inset-0 bg-[#0F172A]/60" onClick={onCancel} data-id="element-1252" />
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full" data-id="element-1253">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-1254">
          Discard changes?
        </h3>
        <p className="text-sm text-[#64748B] mb-6" data-id="element-1255">
          Any information entered will be lost.
        </p>
        <div className="flex gap-3 justify-end" data-id="element-1256">
          <Button variant="outline" size="md" onClick={onCancel} data-id="element-1257">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={onDiscard} data-id="element-1258">
            Discard
          </Button>
        </div>
      </div>
    </div>;
}